import { DropdownItem, DropdownMenu, IconButton } from '@dev-spendesk/grapes';
import { useHistory } from 'react-router-dom';

import { NotificationType, useNotifications } from 'modules/app/notifications';
import {
  type I18nKey,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';
import { routeFor, routes } from 'src/core/constants/routes';
import { budgetIdPageParameter } from 'src/core/modules/budgets/budgets-by-dimensions/hooks/useBudgetIdPageParameter';
import { useDeleteBudgetDefinition } from 'src/core/modules/budgets/budgets-by-dimensions/hooks/useDeleteBudgetDefinition';

type BudgetActionsDropdownProps = {
  budgetDefinitionId: string;
  companyId: string;
};

export const BudgetActionsDropdown = ({
  budgetDefinitionId,
  companyId,
}: BudgetActionsDropdownProps) => {
  const { t } = useTranslation('global');
  const history = useHistory();
  const { pushNotif } = useNotifications();

  const deleteBudgetDefinitionMutation =
    useDeleteBudgetDefinition(budgetDefinitionId);

  const handleUpdateBudget = () => {
    const pathname = routeFor(routes.SETUP_BUDGET_BY_DIMENSIONS.path, {
      company: companyId,
    });
    history.push({
      pathname,
      state: budgetIdPageParameter.getLocationState(budgetDefinitionId),
    });
  };

  const handleDeleteBudget = async () => {
    try {
      await deleteBudgetDefinitionMutation.mutateAsync();
      pushNotif({
        type: NotificationType.Success,
        message: t('budget.redesigned.delete.notifySuccess'),
      });
      history.push(
        routeFor(routes.BUDGETS_V2.path, {
          company: companyId,
        }),
      );
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('budget.redesigned.delete.error'),
      });
    }
  };

  const menuOptions: {
    key: 'edit' | 'delete';
    hasSeparator: boolean;
    action: () => void | Promise<void>;
  }[] = [
    {
      key: 'edit',
      hasSeparator: false,
      action: handleUpdateBudget,
    },
    {
      key: 'delete',
      hasSeparator: false,
      action: handleDeleteBudget,
    },
  ];

  return (
    <DropdownMenu
      options={menuOptions.map((option) => ({
        label: t(dropdownOptionToI18NKey[option.key]),
        ...option,
      }))}
      placement="bottom-end"
      renderButton={(getToggleButtonProps) => {
        return (
          <IconButton
            {...getToggleButtonProps()}
            iconName="ellipsis-vertical"
            variant="secondaryNeutral"
            aria-label={t('misc.openMenu')}
          />
        );
      }}
      renderOption={(option) => (
        <>
          <div className="w-[296px]">
            <DropdownItem
              label={option.label}
              className={
                option.key === 'delete' ? 'text-content-alert-default' : ''
              }
            />
          </div>
          {option.hasSeparator && <div className="separator my-4" />}
        </>
      )}
      onSelect={(option) => option.action()}
    />
  );
};

const dropdownOptionToI18NKey: Record<'edit' | 'delete', I18nKey> = {
  edit: 'budget.redesigned.editBudget',
  delete: 'budget.redesigned.deleteBudget',
};
